import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError } from '../../../lib/api'
import { env } from '../../../lib/env'
import { resendVerificationEmail } from '../api'
import { AuthAlert } from '../components/AuthAlert'
import { AuthSplitShell } from '../components/AuthSplitShell'
import { PublicPageControls } from '@/components/app/PublicPageControls'
import { StateSurface, StateSurfaceSkeleton } from '@/components/app/StateSurface'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Mail, RefreshCw } from 'lucide-react'

export function EmailVerificationPendingPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? ''
  const verified = searchParams.get('verified') === '1'
  const verificationUrl = searchParams.get('verification_url')

  const [resent, setResent] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const resendMutation = useMutation({
    mutationFn: resendVerificationEmail,
    onSuccess: () => {
      setResent(true)
      setErrorMessage(null)
    },
    onError: (error) => {
      setErrorMessage((error as ApiError).message)
    },
  })

  useEffect(() => {
    if (!verificationUrl) {
      return
    }

    try {
      const target = new URL(verificationUrl)
      const apiOrigin = new URL(env.apiBaseUrl).origin

      if (target.origin === apiOrigin && target.pathname.includes('/auth/email/verify/')) {
        window.location.replace(target.toString())
      }
    } catch {
      // Keep the user on the verification screen if the link is malformed.
    }
  }, [verificationUrl])

  if (verificationUrl && !verified) {
    return <StateSurfaceSkeleton variant="fullscreen" size="lg" />
  }

  const content = (
    <Card className="w-full border-none bg-transparent shadow-none">
      <CardHeader className="space-y-4 px-0 pt-0">
        <div className="space-y-1.5">
          <CardTitle className="text-2xl font-semibold tracking-tight">
            {verified ? t('auth.emailVerified') : t('auth.verifyYourEmail')}
          </CardTitle>
          <CardDescription className="text-sm leading-6">
            {verified
              ? t('auth.emailVerifiedDesc')
              : t('auth.verifyYourEmailDesc')}
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent className="space-y-5 px-0 pb-0">
        {!verified ? (
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-4">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {t('auth.checkYourInbox')}
                </p>
                <p className="text-sm leading-6 text-muted-foreground">
                  {email
                    ? `${t('auth.verificationLinkSent')} ${email}. ${t('auth.clickToActivate')}`
                    : t('auth.verificationLinkSentEmail')}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {resent ? (
          <AuthAlert variant="success">
            {t('auth.verificationEmailResent')}
          </AuthAlert>
        ) : null}

        {errorMessage ? (
          <AuthAlert variant="error">
            {errorMessage}
          </AuthAlert>
        ) : null}

        {email && !verified ? (
          <Button
            variant="outline"
            className="h-10 w-full"
            disabled={resendMutation.isPending || resent}
            onClick={() => resendMutation.mutate({ email })}
          >
            <RefreshCw className="mr-2 size-4" />
            {resendMutation.isPending
              ? t('auth.sending')
              : t('auth.resendVerificationEmail')}
          </Button>
        ) : null}

        <Button asChild size="lg" className="w-full">
          <Link to="/login">{t('auth.signIn')}</Link>
        </Button>

        <div className="flex justify-center pt-2">
          <Link
            to="/login"
            className="text-sm font-medium text-foreground underline underline-offset-4 transition hover:text-foreground/80"
          >
            {t('auth.returnToLogin')}
          </Link>
        </div>
      </CardContent>
    </Card>
  )

  return verified ? (
    <div className="relative">
      <div className="absolute right-4 top-4 z-20 sm:right-6 sm:top-6">
        <PublicPageControls />
      </div>
      <StateSurface
        variant="fullscreen"
        size="lg"
        tone="success"
        asset={(
          <img
            src="/Uploads/email_verified.webp"
            alt={t('auth.emailVerified')}
            className="h-56 w-56 object-contain md:h-64 md:w-64"
          />
        )}
        title={t('auth.emailVerified')}
        description={t('auth.emailVerifiedDesc')}
        primaryAction={{
          label: t('auth.signIn'),
          to: '/login',
        }}
      />
    </div>
  ) : (
    <AuthSplitShell>{content}</AuthSplitShell>
  )
}
